// Suppress React 18 + Material-UI v5 false positive prop type warnings
// MUST be at the very top before any imports to catch warnings early
// These warnings are false positives caused by React 18's stricter prop type validation
// combined with Material-UI v5's prop type definitions. They don't affect functionality.
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  // Material-UI component names that trigger false positive warnings
  const muiComponents = [
    'ThemeProvider', 'ThemeProvider2', 'StyledEngineProvider', 'RtlProvider', 'DefaultPropsProvider',
    'Box', 'Typography', 'Container', 'Tooltip', 'IconButton', 'ButtonBase',
    'SvgIcon', 'InputAdornment', 'TextField', 'FormControl', 'Dialog',
    'DialogContent', 'DialogActions', 'Modal', 'Fade', 'Paper', 'Alert',
    'FormControlLabel', 'FormGroup', 'RadioGroup', 'SwitchBase',
    'TransitionGroup', 'TouchRipple', 'OutlinedInput', 'InputBase'
  ];
  
  const shouldSuppressWarning = (message: string): boolean => {
    // Very flexible pattern matching to catch all variations
    const hasPropTypeKeywords = 
      message.includes('Invalid prop') || 
      message.includes('Failed prop type') ||
      message.includes('prop type');
    
    const hasReactNodeKeywords = 
      message.includes('ReactNode') || 
      message.includes('children') ||
      message.includes('expected');
    
    const hasMUIComponent = muiComponents.some(component => message.includes(component));
    const isFromInstallHook = message.includes('installHook');
    
    // Suppress if it's a prop type warning about ReactNode/children for MUI components
    return hasPropTypeKeywords && hasReactNodeKeywords && (hasMUIComponent || isFromInstallHook);
  };
  
  // Intercept console.warn
  const originalWarn = console.warn;
  console.warn = (...args: any[]) => {
    const fullMessage = args.map(arg => String(arg)).join(' ');
    if (shouldSuppressWarning(fullMessage)) {
      return; // Suppress the warning
    }
    originalWarn.apply(console, args);
  };
  
  // Also intercept console.error in case warnings are logged there
  const originalError = console.error;
  console.error = (...args: any[]) => {
    const fullMessage = args.map(arg => String(arg)).join(' ');
    if (shouldSuppressWarning(fullMessage)) {
      return; // Suppress the warning
    }
    originalError.apply(console, args);
  };
}

import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import { StyledEngineProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { ThemeProvider, ThemedMuiProvider } from './theme';
import { registerStaleLazyChunkRecovery } from './utilities/staleLazyChunkRecovery';

import "./global.css";

registerStaleLazyChunkRecovery();

const emotionCache = createCache({
  key: "emotion-cache",
  prepend: true,
});

const container = document.getElementById("root");
const root = createRoot(container!);

root.render(
  <BrowserRouter
    future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    }}
  >
    <StyledEngineProvider injectFirst>
      <CacheProvider value={emotionCache}>
        <ThemeProvider>
          <ThemedMuiProvider>
            <App />
          </ThemedMuiProvider>
        </ThemeProvider>
      </CacheProvider>
    </StyledEngineProvider>
  </BrowserRouter>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();